"use client";
import { getDemoTemplate } from "@ocr-monorepo/shared/demo-templates";
import { publicErrorMessage } from "@ocr-monorepo/shared";

import {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useState,
} from "react";
import { useParams, useSearchParams } from "next/navigation";
import { useTRPC } from "@/lib/trpc";
import { useQuery } from "@tanstack/react-query";
import { useSubscription } from "@trpc/tanstack-react-query";
import { client as authClient } from "@/lib/auth-client";

type SetupStep =
  | "check_session"
  | "do_auth"
  | "checking"
  | "initializing"
  | "creating-project"
  | "configuring-limits"
  | "copying-files"
  | "copied-files"
  | "complete"
  | "error"
  | "not-found"
  | "expired";

interface DemoContextValue {
  sessionId: string | null;
  projectSlug: string | null;
  isInitialized: boolean;
  setupStep: SetupStep;
  error: string | null;
  tableSlug: string | null;
  copyProgress: { copied: number; total: number; percent: number } | null;
}

const DemoContext = createContext<DemoContextValue>({
  sessionId: null,
  projectSlug: null,
  isInitialized: false,
  setupStep: "check_session",
  error: null,
  copyProgress: null,
  tableSlug: null,
});

export function useDemoContext() {
  const context = useContext(DemoContext);
  if (!context) {
    throw new Error("useDemoContext must be used within DemoProvider");
  }
  return context;
}

interface DemoProviderProps {
  children: ReactNode;
  enabled?: boolean;
}
/**
 * Demo provider that handles session initialization and provides demo context
 */
export function DemoProvider({ children, enabled = false }: DemoProviderProps) {
  const trpc = useTRPC();
  const params = useParams<{
    previewType?: string;
  }>();
  const [sessionState, setSessionState] = useState<SetupStep>("check_session");
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [tableSlug, setTableSlug] = useState<string | null>(null);
  const [projectSlug, setProjectSlug] = useState<string | null>(null);
  const [copyProgress, setCopyProgress] = useState<{
    copied: number;
    total: number;
    percent: number;
  } | null>(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const searchParams = useSearchParams();

  const previewType =
    getDemoTemplate(params.previewType ?? "")?.id ?? "blank-space";
  const vaultQuery = useQuery(trpc.projects.vaultEnabled.queryOptions());
  const vaultEnabledForEnv = vaultQuery.data?.enabled ?? false;
  const isVaultStateReady = vaultQuery.isSuccess || vaultQuery.isError;
  const passwordProtectionEnabled = vaultEnabledForEnv
    ? searchParams?.get("passwordProtection") === "1"
    : false;

  useEffect(() => {
    if (!enabled) return;
    let isMounted = true;

    const ensureAuth = async () => {
      setSessionState("do_auth");

      try {
        const existingSession = await authClient.getSession();
        if (!isMounted) {
          return;
        }

        if (!existingSession?.data) {
          const result = await authClient.signIn.anonymous();
          if (!isMounted) {
            return;
          }

          if (result?.error) {
            throw new Error(publicErrorMessage("system_error"));
          }
        }

        setIsAuthReady(true);
        setSessionState("check_session");
      } catch (err) {
        if (!isMounted) {
          return;
        }
        const message =
          err instanceof Error
            ? err.message
            : "Failed to authenticate demo user";
        setSessionState("error");
        setError(message);
      }
    };

    ensureAuth();

    return () => {
      isMounted = false;
    };
  }, [enabled]);

  useSubscription(
    trpc.demo.initSession.subscriptionOptions(
      { previewType, passwordProtectionEnabled },
      {
        enabled: enabled && isAuthReady && isVaultStateReady,
        onData: (update) => {
          setSessionState(update.step);

          if (update.step === "copying-files") {
            const data = update.data as
              { copied?: number; total?: number; percent?: number } | undefined;
            if (data?.total) {
              setCopyProgress({
                copied: data.copied ?? 0,
                total: data.total,
                percent: data.percent ?? 0,
              });
            }
          } else {
            setCopyProgress(null);
          }

          if (update.data && "sessionId" in update.data) {
            setSessionId(update.data.sessionId ?? null);
            setProjectSlug(update.data.projectSlug ?? null);
            setTableSlug(update.data.tableSlug ?? null);
          }
        },
        onError: (err) => {
          setSessionState("error");
          setError(err.message || "Failed to initialize demo");
        },
      },
    ),
  );

  const contextValue: DemoContextValue = {
    sessionId,
    projectSlug,
    tableSlug,
    isInitialized: sessionState === "complete",
    setupStep: sessionState,
    error,
    copyProgress,
  };

  return (
    <DemoContext.Provider value={contextValue}>{children}</DemoContext.Provider>
  );
}
